"use client"
import { FaChevronDown, FaSearch, FaTimes, FaSpinner } from 'react-icons/fa';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import { LuChevronDown, LuSearch, LuX, LuLoader } from "react-icons/lu"
import axiosInstance from "../libs/axios"

// Tallest the open menu gets (max-h-60). Used to decide whether it fits below.
const PORTAL_MENU_HEIGHT = 240

/**
 * Position for a menu rendered into document.body (`portalMenu`). A select
 * inside a scrolling container — a modal body — gets its absolute menu clipped
 * by that container, so the user has to scroll the container to reach the
 * options. A portalled menu sits above everything instead: it is pinned under
 * its trigger, flips above it when there is no room below, and follows the
 * trigger while any ancestor scrolls or the window resizes.
 */
const usePortalMenuStyle = (
  enabled: boolean,
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>
): React.CSSProperties | null => {
  const [style, setStyle] = useState<React.CSSProperties | null>(null)

  useLayoutEffect(() => {
    if (!enabled || !isOpen) {
      setStyle(null)
      return
    }
    const update = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpward = spaceBelow < PORTAL_MENU_HEIGHT + 8 && rect.top > spaceBelow
      setStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        zIndex: 1000,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      })
    }
    update()
    // Capture phase so a scroll of any ancestor (the modal body) is seen.
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [enabled, isOpen, anchorRef])

  return style
}

export interface SelectOption {
  id: number
  label: string
  value: string | number
}

interface PaginationData {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

interface ApiResponse<T> {
  success: boolean
  message: string
  data: {
    states?: T[]
    districts?: T[]
    sites?: T[]
    pagination: PaginationData
  }
  statusCode?: number
  status_code?: number
}

interface SelectWithSearchProps {
  placeholder?: string
  value?: SelectOption | null
  onChange: (option: SelectOption | null) => void
  disabled?: boolean
  className?: string
  options?: SelectOption[] // For static options
  apiEndpoint?: string // Optional for dynamic
  dataKey?: 'states' | 'districts' | 'sites' | any
  searchParam?: string // 'search' for districts
  dependencyId?: number | null // state_circle_id for districts
  extraParams?: Record<string, string | number | boolean | undefined | null>
  labelKey?: string // 'state_name' for states, 'name' for districts
  valueKey?: string // 'id' for both
  displayFormat?: (item: any) => string // custom display format
  closeOnOutsideClick?: boolean
  maxSearchWords?: number
  minSearchLength?: number
  /** Render the open menu above the page (see usePortalMenuStyle). For selects inside a scrolling modal. */
  portalMenu?: boolean
}

export const SelectWithSearch: React.FC<SelectWithSearchProps> = ({
  placeholder = "Select...",
  value,
  onChange,
  disabled = false,
  className = "",
  options: staticOptions,
  apiEndpoint,
  dataKey,
  searchParam = "search",
  dependencyId,
  extraParams,
  labelKey = "name",
  valueKey = "id",
  displayFormat,
  closeOnOutsideClick = true,
  maxSearchWords,
  minSearchLength,
  portalMenu = false,
}) => {
  const effectiveMinLength = minSearchLength !== undefined ? minSearchLength : (apiEndpoint && !staticOptions ? 3 : 0)
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [options, setOptions] = useState<SelectOption[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [loadingMore, setLoadingMore] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const lastOptionRef = useRef<HTMLDivElement>(null)
  const prevDependencyId = useRef(dependencyId)
  const portalMenuStyle = usePortalMenuStyle(portalMenu, isOpen, dropdownRef)
  // The trigger and a portalled menu live in different DOM trees; both count
  // as "inside" for outside-click detection.
  const isInsideSelect = (target: EventTarget | null) =>
    Boolean(
      dropdownRef.current?.contains(target as Node) || menuRef.current?.contains(target as Node)
    )
  const pointerDownInfo = useRef<{ x: number; y: number } | null>(null)
  const pointerDownStartedInside = useRef(false)
  const extraParamsKey = JSON.stringify(extraParams ?? {})
  const stableExtraParams = useMemo(() => extraParams ?? {}, [extraParamsKey])
  const normalizedSearchTerm = useMemo(() => {
    const trimmed = searchTerm.trim()
    if (!trimmed) return ""

    const words = trimmed.split(/\s+/)
    if (!maxSearchWords || maxSearchWords <= 0) {
      return words.join(" ")
    }

    return words.slice(0, maxSearchWords).join(" ")
  }, [searchTerm, maxSearchWords])

  // Set static options if provided
  useEffect(() => {
    if (staticOptions) {
      setOptions(staticOptions)
    }
  }, [staticOptions])

  // Reset when dependency changes (for districts when state changes)
  useEffect(() => {
    if (dependencyId !== undefined && dependencyId !== null && !staticOptions && prevDependencyId.current !== dependencyId && prevDependencyId.current !== null && prevDependencyId.current !== undefined) {
      setOptions([])
      setPage(1)
      setHasMore(true)
      setSearchTerm("")
      if (value) {
        onChange(null)
      }
    }
    prevDependencyId.current = dependencyId
  }, [dependencyId, staticOptions])

  // Fetch data function
  const fetchData = useCallback(async (pageNum: number, search: string, append = false) => {
    if (staticOptions || !apiEndpoint || !dataKey) return // No fetch for static options

    if (!dependencyId && dataKey === 'districts') return

    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '10',
      })

      if (stableExtraParams && Object.keys(stableExtraParams).length > 0) {
        Object.entries(stableExtraParams).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") return
          params.append(key, String(value))
        })
      }

      if (search.trim()) {
        params.append(searchParam, search.trim())
      }

      let url = apiEndpoint
      if (dataKey === 'districts' && dependencyId) {
        url = `${apiEndpoint}/${dependencyId}/districts`
      }

      const response = await axiosInstance.get(`${url}?${params}`)
      const data: ApiResponse<any> = response.data

      if (data.success && data.data) {
        const collection = (data as any).data[dataKey]

        if (Array.isArray(collection)) {
          const newOptions: SelectOption[] = collection.map((item: any) => ({
            id: item[valueKey],
            label: displayFormat ? displayFormat(item) : item[labelKey],
            value: item[valueKey],
          }))

          if (append) {
            // Drop ids we already hold: an endpoint that ignores `page` would
            // otherwise keep re-appending its first page as the user scrolls.
            setOptions(prev => {
              const seen = new Set(prev.map(option => option.id))
              return [...prev, ...newOptions.filter(option => !seen.has(option.id))]
            })
          } else {
            setOptions(newOptions)
          }

          // Endpoints that return no pagination block are single-page by
          // definition — never keep asking them for more.
          setHasMore(data.data.pagination?.has_next_page ?? false)
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }, [apiEndpoint, dataKey, dependencyId, stableExtraParams, labelKey, valueKey, displayFormat, searchParam, staticOptions])

  // Initial load - only for static options or districts with dependency
  useEffect(() => {
    if (staticOptions) return
    // For API-based dropdowns: no initial load, wait for 3+ chars
  }, [dependencyId, dataKey, fetchData, staticOptions])

  // Search effect - trigger when search term meets minSearchLength threshold
  useEffect(() => {
    if (staticOptions) return // No search for static options

    const debounceTimer = setTimeout(() => {
      const canSearch = dataKey !== 'districts' || Boolean(dependencyId)
      if (!canSearch) return
      if (normalizedSearchTerm.length >= effectiveMinLength) {
        setLoading(true)
        fetchData(1, normalizedSearchTerm).finally(() => setLoading(false))
        setPage(1)
        setHasMore(true)
      } else {
        setOptions([])
      }
    }, 300)

    return () => clearTimeout(debounceTimer)
  }, [normalizedSearchTerm, dependencyId, dataKey, fetchData, staticOptions])

  // Initial load when dropdown opens and minSearchLength is 0
  useEffect(() => {
    if (staticOptions || !apiEndpoint || effectiveMinLength > 0) return
    if (isOpen && options.length === 0 && !loading) {
      setLoading(true)
      fetchData(1, "").finally(() => setLoading(false))
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll
  useEffect(() => {
    if (staticOptions) return // No infinite scroll for static options

    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && isOpen) {
          setLoadingMore(true)
          const nextPage = page + 1
          fetchData(nextPage, normalizedSearchTerm, true).finally(() => {
            setLoadingMore(false)
            setPage(nextPage)
          })
        }
      },
      { threshold: 0.1 }
    )

    if (lastOptionRef.current && observerRef.current) {
      observerRef.current.observe(lastOptionRef.current)
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect()
    }
  }, [hasMore, loadingMore, page, normalizedSearchTerm, fetchData, isOpen, staticOptions])

  // Click outside to close
  useEffect(() => {
    if (!closeOnOutsideClick) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      pointerDownInfo.current = { x: event.clientX, y: event.clientY }
      pointerDownStartedInside.current = isInsideSelect(event.target)
    }

    const handlePointerUp = (event: PointerEvent) => {
      if (!isOpen || !dropdownRef.current) {
        pointerDownInfo.current = null
        pointerDownStartedInside.current = false
        return
      }

      const info = pointerDownInfo.current
      pointerDownInfo.current = null
      const startedInside = pointerDownStartedInside.current
      pointerDownStartedInside.current = false

      const movedDistance = info
        ? Math.hypot(event.clientX - info.x, event.clientY - info.y)
        : 0

      const clickedInside = isInsideSelect(event.target)

      if (!clickedInside && !startedInside && movedDistance < 5) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    const handlePointerCancel = () => {
      pointerDownInfo.current = null
      pointerDownStartedInside.current = false
    }

    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("pointerup", handlePointerUp)
    document.addEventListener("pointercancel", handlePointerCancel)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("pointerup", handlePointerUp)
      document.removeEventListener("pointercancel", handlePointerCancel)
    }
  }, [isOpen, closeOnOutsideClick]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (option: SelectOption) => {
    onChange(option)
    setIsOpen(false)
    setSearchTerm("")
  }

  const handleOptionPointerDown = (event: React.PointerEvent, option: SelectOption) => {
    event.preventDefault()
    event.stopPropagation()
    handleSelect(option)
  }

  const handleClear = () => {
    onChange(null)
    setSearchTerm("")
  }

  const filteredOptions = staticOptions
    ? options.filter(option =>
      option.label?.toLowerCase().includes(normalizedSearchTerm?.toLowerCase())
    )
    : options

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger */}
      <div
        className={`relative w-full px-3 py-3 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
          } ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <span className={`text-sm ${value ? 'text-gray-900' : 'text-gray-500'}`}>
            {value ? value.label : placeholder}
          </span>
          <div className="flex items-center gap-2">
            {value && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
                className="p-0.5 hover:bg-gray-200 rounded transition-colors"
              >
                <LuX className="w-3 h-3 text-gray-500" />
              </button>
            )}
            <LuChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (!portalMenu || portalMenuStyle) && renderMenu(
        portalMenu,
        portalMenuStyle,
        menuRef,
        <>
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <LuSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded  focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-48 overflow-y-auto">
            {!staticOptions && effectiveMinLength > 0 && searchTerm.length < effectiveMinLength ? (
              <div className="py-4 px-3 text-sm text-gray-400 text-center">
                Type at least {effectiveMinLength} characters to search
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-4">
                <LuLoader className="w-5 h-5 animate-spin text-gray-500" />
                <span className="ml-2 text-sm text-gray-500">Loading...</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="py-4 px-3 text-sm text-gray-500 text-center">
                No options found
              </div>
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <div
                    key={option.id}
                    ref={index === filteredOptions.length - 1 ? lastOptionRef : null}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors ${value?.id === option.id ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                      }`}
                    onPointerDown={(event) => handleOptionPointerDown(event, option)}
                  >
                    {option.label}
                  </div>
                ))}
                {loadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <LuLoader className="w-4 h-4 animate-spin text-gray-500" />
                    <span className="ml-2 text-xs text-gray-500">Loading more...</span>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * The open menu box: absolute under the trigger as before, or — with
 * `portalMenu` — fixed-positioned in document.body (see usePortalMenuStyle).
 */
const renderMenu = (
  portal: boolean,
  portalStyle: React.CSSProperties | null,
  menuRef: React.RefObject<HTMLDivElement | null>,
  children: React.ReactNode
) => {
  const boxClass = "bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-hidden"
  if (portal && portalStyle) {
    return createPortal(
      <div ref={menuRef} style={portalStyle} className={boxClass}>
        {children}
      </div>,
      document.body
    )
  }
  return (
    <div ref={menuRef} className={`absolute z-50 w-full mt-1 ${boxClass}`}>
      {children}
    </div>
  )
}


interface SelectWithSearch2Props {
  placeholder?: string
  value?: SelectOption | null
  onChange: (option: SelectOption | null) => void
  disabled?: boolean
  className?: string
  options?: SelectOption[] // For static options
  apiEndpoint?: string // Optional for dynamic
  dataKey?: 'states' | 'districts' | any
  searchParam?: string // 'search' for districts
  dependencyId?: number | null // state_circle_id for districts
  labelKey?: string // 'state_name' for states, 'name' for districts
  valueKey?: string // 'id' for both
  displayFormat?: (item: any) => string // custom display format
  extraParams?: Record<string, any>
  mapOption?: (item: any) => SelectOption
  minSearchLength?: number
}

export const SelectWithSearch2: React.FC<SelectWithSearch2Props> = ({
  extraParams,
  mapOption,
  placeholder = "Select...",
  value,
  onChange,
  disabled = false,
  className = "",
  options: staticOptions,
  apiEndpoint,
  dataKey,
  searchParam = "search",
  dependencyId,
  labelKey = "name",
  valueKey = "id",
  displayFormat,
  minSearchLength,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [options, setOptions] = useState<SelectOption[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [loadingMore, setLoadingMore] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const lastOptionRef = useRef<HTMLDivElement>(null)
  const prevDependencyId = useRef(dependencyId)
  const pointerDownInfo = useRef<{ x: number; y: number } | null>(null)
  const effectiveMinSearchLength = minSearchLength !== undefined && minSearchLength !== null
    ? minSearchLength
    : (apiEndpoint ? 3 : 0)
  // Force at least 1 character so an API search never runs on mount or on open —
  // it only runs once the user actually types a query.
  const minSearchChars = Math.max(effectiveMinSearchLength, 1)

  // Set static options if provided
  useEffect(() => {
    if (staticOptions) {
      setOptions(staticOptions)
    }
  }, [staticOptions])

  // Reset when dependency changes (for districts when state changes)
  useEffect(() => {
    if (dependencyId !== undefined && dependencyId !== null && !staticOptions && prevDependencyId.current !== dependencyId && prevDependencyId.current !== null && prevDependencyId.current !== undefined) {
      setOptions([])
      setPage(1)
      setHasMore(true)
      setSearchTerm("")
      if (value) {
        onChange(null)
      }
    }
    prevDependencyId.current = dependencyId
  }, [dependencyId, staticOptions])

  // Fetch data function
  const fetchData = useCallback(async (pageNum: number, search: string, append = false) => {
    if (staticOptions || !apiEndpoint) return // No fetch for static options

    try {
      const effectiveLimit = (extraParams && typeof extraParams.limit === 'number' && extraParams.limit > 0)
        ? String(extraParams.limit)
        : '10'
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: effectiveLimit,
      })

      if (search.trim()) {
        params.append(searchParam, search.trim())
      }

      if (extraParams) {
        Object.entries(extraParams).forEach(([key, value]) => {
          if (key === 'limit') return // already set above
          if (value !== undefined && value !== null && value !== "") {
            // Handle array values (like role_ids)
            if (Array.isArray(value)) {
              value.forEach(item => {
                params.append(key, String(item))
              })
            } else {
              params.append(key, String(value))
            }
          }
        })
      }

      const finalUrl = `${apiEndpoint}?${params}`

      const response = await axiosInstance.get(finalUrl)
      const data: ApiResponse<any> = response.data

      const collection = dataKey ? (data as any).data[dataKey] : (data as any).data?.users

      if (data.success && Array.isArray(collection)) {
        const newOptions: SelectOption[] = collection.map((item: any) => {
          if (mapOption) {
            return mapOption(item)
          }

          if (dataKey) {
            return {
              id: item[valueKey],
              label: displayFormat ? displayFormat(item) : item[labelKey],
              value: item[valueKey],
            }
          }

          return {
            id: item.id,
            label: displayFormat ? displayFormat(item) : item[labelKey],
            value: item.id,
          }
        })

        if (append) {
          setOptions(prev => [...prev, ...newOptions])
        } else {
          setOptions(newOptions)
        }

        const hasNext = data.data?.pagination?.has_next_page ?? false
        setHasMore(hasNext)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }, [apiEndpoint, dataKey, displayFormat, labelKey, mapOption, staticOptions, valueKey, searchParam, extraParams])

  // Search effect - only fetch in response to the user typing a query.
  // No fetch on mount and no fetch when the dropdown opens.
  useEffect(() => {
    if (staticOptions || !apiEndpoint) return // No search for static options

    const trimmed = searchTerm.trim()
    if (trimmed.length < minSearchChars) {
      setOptions([])
      setHasMore(true)
      setPage(1)
      return
    }

    const debounceTimer = setTimeout(() => {
      setLoading(true)
      fetchData(1, trimmed).finally(() => setLoading(false))
      setPage(1)
      setHasMore(true)
    }, 300)

    return () => clearTimeout(debounceTimer)
  }, [searchTerm, apiEndpoint, minSearchChars, fetchData, staticOptions])

  // Infinite scroll
  useEffect(() => {
    if (staticOptions) return // No infinite scroll for static options

    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && isOpen) {
          setLoadingMore(true)
          const nextPage = page + 1
          fetchData(nextPage, searchTerm, true).finally(() => {
            setLoadingMore(false)
            setPage(nextPage)
          })
        }
      },
      { threshold: 0.1 }
    )

    if (lastOptionRef.current && observerRef.current) {
      observerRef.current.observe(lastOptionRef.current)
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect()
    }
  }, [hasMore, loadingMore, page, searchTerm, fetchData, isOpen, staticOptions])

  // Click outside to close with scroll-safe detection
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      pointerDownInfo.current = { x: event.clientX, y: event.clientY }
    }

    const handlePointerUp = (event: PointerEvent) => {
      if (!isOpen || !dropdownRef.current) {
        pointerDownInfo.current = null
        return
      }

      const info = pointerDownInfo.current
      pointerDownInfo.current = null

      const movedDistance = info
        ? Math.hypot(event.clientX - info.x, event.clientY - info.y)
        : 0

      const clickedInside = dropdownRef.current.contains(event.target as Node)

      if (!clickedInside && movedDistance < 5) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    const handlePointerCancel = () => {
      pointerDownInfo.current = null
    }

    document.addEventListener("pointerdown", handlePointerDown)
    document.addEventListener("pointerup", handlePointerUp)
    document.addEventListener("pointercancel", handlePointerCancel)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
      document.removeEventListener("pointerup", handlePointerUp)
      document.removeEventListener("pointercancel", handlePointerCancel)
    }
  }, [isOpen])

  const handleSelect = (option: SelectOption) => {
    onChange(option)
    setIsOpen(false)
    setSearchTerm("")
  }

  const handleClear = () => {
    onChange(null)
    setSearchTerm("")
  }

  const filteredOptions = staticOptions
    ? options.filter(option =>
      option.label?.toLowerCase().includes(searchTerm?.toLowerCase())
    )
    : options

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger */}
      <div
        className={`relative w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
          } ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <span className={`text-sm ${value ? 'text-gray-900' : 'text-gray-500'}`}>
            {value ? value.label : placeholder}
          </span>
          <div className="flex items-center gap-2">
            {value && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear()
                }}
                className="p-0.5 hover:bg-gray-200 rounded transition-colors"
              >
                <LuX className="w-3 h-3 text-gray-500" />
              </button>
            )}
            <LuChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded  shadow-lg max-h-60 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <LuSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded  focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-48 overflow-y-auto">
            {!staticOptions && searchTerm.trim().length < minSearchChars ? (
              <div className="py-4 px-3 text-sm text-gray-400 text-center">
                Type at least {minSearchChars} character{minSearchChars > 1 ? 's' : ''} to search
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-4">
                <LuLoader className="w-5 h-5 animate-spin text-gray-500" />
                <span className="ml-2 text-sm text-gray-500">Loading...</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="py-4 px-3 text-sm text-gray-500 text-center">
                No options found
              </div>
            ) : (
              <>
                {filteredOptions.map((option, index) => (
                  <div
                    key={option.id}
                    ref={index === filteredOptions.length - 1 ? lastOptionRef : null}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors ${value?.id === option.id ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                      }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                  </div>
                ))}
                {loadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <LuLoader className="w-4 h-4 animate-spin text-gray-500" />
                    <span className="ml-2 text-xs text-gray-500">Loading more...</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}


interface Option {
  value: string | number;
  label: string;
  data?: any;
}

interface SearchableSelectProps {
  value: Option | Option[] | null;
  onChange: (option: any) => void;
  options?: Option[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  isLoading?: boolean;
  onSearch?: (query: string) => void;
  debounceMs?: number;
  emptyMessage?: string;
  className?: string;
  isMulti?: boolean;
  showSelectAllVisible?: boolean;
  closeOnOutsideClick?: boolean;
  minSearchLength?: number;
  /** Render the open menu above the page (see usePortalMenuStyle). For selects inside a scrolling modal. */
  portalMenu?: boolean;
}

export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  disabled = false,
  error,
  isLoading = false,
  onSearch,
  debounceMs = 300,
  emptyMessage = "No options found",
  className = "",
  isMulti = false,
  showSelectAllVisible = false,
  closeOnOutsideClick = true,
  minSearchLength = 0,
  portalMenu = false,
}: SearchableSelectProps) {

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const portalMenuStyle = usePortalMenuStyle(portalMenu, isOpen, containerRef);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Minimum characters before the parent's onSearch is called. Force at least 1
  // so a server-backed search never fires on mount or open — only on typing.
  const searchThreshold = Math.max(minSearchLength || 0, 1);

  // Update filtered options when options change
  useEffect(() => {
    if (!onSearch) {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [options, searchQuery, onSearch]);

  const searchCallbackRef = useRef(onSearch);
  useEffect(() => {
    searchCallbackRef.current = onSearch;
  }, [onSearch]);

  // Handle search with debouncing — only fires the parent search once the user
  // has typed enough characters (never on mount / open).
  useEffect(() => {
    if (searchQuery.length >= searchThreshold && searchCallbackRef.current) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (searchCallbackRef.current) {
          searchCallbackRef.current(searchQuery);
        }
      }, debounceMs);

      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }
  }, [searchQuery, debounceMs, searchThreshold]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!closeOnOutsideClick) return

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      // A portalled menu is outside containerRef but still part of the select.
      if (menuRef.current?.contains(target)) return
      // With the menu portalled, the field scrolls with its modal and the menu
      // follows it, so pressing that modal's scrollbar must not close it.
      if (portalMenu && event.target instanceof HTMLElement && event.offsetX > event.target.clientWidth) return
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false)
        setSearchQuery("")
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [closeOnOutsideClick, portalMenu]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery("");
      }
    }
  };

  const handleSelect = (option: Option) => {
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.some(v => v.value === option.value);
      if (isSelected) {
        onChange(currentValues.filter(v => v.value !== option.value));
      } else {
        onChange([...currentValues, option]);
      }
    } else {
      onChange(option);
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(isMulti ? [] : null);
    setSearchQuery("");
  };

  const isOptionSelected = (option: Option) => {
    if (isMulti) {
      return Array.isArray(value) && value.some(v => v.value === option.value);
    }
    return !Array.isArray(value) && value?.value === option.value;
  };

  const getDisplayValue = () => {
    if (isMulti) {
      if (Array.isArray(value) && value.length > 0) {
        return value.map(v => v.label).join(", ");
      }
      return placeholder;
    }
    return (value && !Array.isArray(value)) ? value.label : placeholder;
  };

  const allVisibleSelected =
    isMulti &&
    filteredOptions.length > 0 &&
    Array.isArray(value) &&
    filteredOptions.every((option) => value.some((selected) => selected.value === option.value));

  const handleSelectAllVisible = () => {
    if (!isMulti) return;

    const currentValues = Array.isArray(value) ? value : [];
    if (allVisibleSelected) {
      onChange(
        currentValues.filter(
          (selected) => !filteredOptions.some((visible) => visible.value === selected.value)
        )
      );
      return;
    }

    const visibleToAdd = filteredOptions.filter(
      (option) => !currentValues.some((selected) => selected.value === option.value)
    );
    onChange([...currentValues, ...visibleToAdd]);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Select Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full px-3 py-2 text-left border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm flex items-center justify-between ${error ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}`}
      >
        <span className={(isMulti ? (Array.isArray(value) && value.length > 0) : (value && !Array.isArray(value))) ? 'text-gray-900' : 'text-gray-500'}>
          {getDisplayValue()}
        </span>
        <div className="flex items-center gap-2">
          {((isMulti && Array.isArray(value) && value.length > 0) || (!isMulti && value && !Array.isArray(value))) && !disabled && (
            <FaTimes
              className="w-4 h-4 text-gray-400 hover:text-gray-600"
              onClick={handleClear}
            />
          )}
          <FaChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''
              }`}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (!portalMenu || portalMenuStyle) && renderMenu(
        portalMenu,
        portalMenuStyle,
        menuRef,
        <>
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-48">
            {isMulti && showSelectAllVisible && filteredOptions.length > 0 && !isLoading && (
              <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onClick={handleSelectAllVisible}
                  className="text-xs font-medium text-blue-700 hover:text-blue-800"
                >
                  {allVisibleSelected ? "Unselect visible" : "Select all visible"}
                </button>
              </div>
            )}
            {onSearch && searchQuery.length < searchThreshold ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                Type at least {searchThreshold} character{searchThreshold > 1 ? 's' : ''} to search
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-8">
                <FaSpinner className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="ml-2 text-sm text-gray-600">Loading...</span>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelect(option);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors ${isOptionSelected(option)
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-900'
                    }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </>
      )}

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}